import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";

import Login from "@/pages/Login";
import ProgramPicker from "@/pages/ProgramPicker";
import AppShell from "@/components/AppShell";
import ConsoleShell from "@/components/ConsoleShell";
import Dashboard from "@/pages/Dashboard";
import Chat from "@/pages/Chat";
import Knowledge from "@/pages/Knowledge";
import Training from "@/pages/Training";
import Downloads from "@/pages/Downloads";
import WhatsNew from "@/pages/WhatsNew";
import Support from "@/pages/Support";
import Analytics from "@/pages/Analytics";
import Admin from "@/pages/Admin";
import ContentManagement from "@/pages/ContentManagement";
import Approvals from "@/pages/Approvals";
import ConsoleOverview from "@/pages/console/ConsoleOverview";
import ConsoleWhatsNew from "@/pages/console/ConsoleWhatsNew";
import ConsoleSupport from "@/pages/console/ConsoleSupport";

function Protected({ children }) {
  const { user } = useAuth();
  if (user === null)
    return (
      <div className="min-h-screen flex items-center justify-center text-muted-foreground">
        Loading…
      </div>
    );
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route
        path="/"
        element={
          <Protected>
            <ProgramPicker />
          </Protected>
        }
      />

      {/* Management Console — cross-program, no active program required */}
      <Route
        path="/manage"
        element={
          <Protected>
            <ConsoleShell />
          </Protected>
        }
      >
        <Route index element={<Navigate to="overview" replace />} />
        <Route path="overview" element={<ConsoleOverview />} />
        <Route path="content" element={<ContentManagement />} />
        <Route path="approvals" element={<Approvals />} />
        <Route path="analytics" element={<Analytics />} />
        <Route path="whats-new" element={<ConsoleWhatsNew />} />
        <Route path="support" element={<ConsoleSupport />} />
        <Route path="admin" element={<Admin />} />
      </Route>

      {/* Program Workspace — scoped to the active program */}
      <Route
        path="/app"
        element={
          <Protected>
            <AppShell />
          </Protected>
        }
      >
        <Route index element={<Navigate to="dashboard" replace />} />
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="chat" element={<Chat />} />
        <Route path="sops" element={<Knowledge key="sop" kind="sop" title="SOP Library" />} />
        <Route path="faqs" element={<Knowledge key="faq" kind="faq" title="FAQs & Workflows" />} />
        <Route path="training" element={<Training />} />
        <Route path="downloads" element={<Downloads />} />
        <Route path="whats-new" element={<WhatsNew />} />
        <Route path="support" element={<Support />} />
        {/* legacy management paths redirect into the console */}
        <Route path="content" element={<Navigate to="/manage/content" replace />} />
        <Route path="approvals" element={<Navigate to="/manage/approvals" replace />} />
        <Route path="analytics" element={<Navigate to="/manage/analytics" replace />} />
        <Route path="admin" element={<Navigate to="/manage/admin" replace />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}
